import {
  EmailMessage,
  EmailMessageWithInbox,
} from "@services/documents/email/types";

interface UserInfo {
  id: string;
  email: string;
  createdAt: Date;
  hasConnectedEmail: boolean;
  organizationName?: string;
}

export const downloadMessagesAsCSV = (
  messages: EmailMessage[],
  searchQuery: string,
  selectedInboxes: UserInfo[]
) => {
  if (messages.length === 0) {
    return;
  }

  // Create CSV headers
  const csvHeaders = [
    "hmid",
    "messageid", 
    "link",
    "Subject Line",
    "date/time",
    "Sender",
    "Receivers"
  ];

  // Generate CSV rows
  const csvRows = messages.map((message) => {
    // Format date as yyyy/mm/dd hh:mm
    const formatDate = (date: Date | string) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}/${month}/${day} ${hours}:${minutes}`;
    };

    // Generate link with headerMessageId
    const generateLink = () => {
      if (!message.headerMessageId) return "";
      
      const url = new URL(window.location.href);
      url.searchParams.set("messageHeaderId", message.headerMessageId);
      
      // Include selected inboxes in the link
      if (selectedInboxes.length > 0) {
        url.searchParams.set(
          "inboxes",
          selectedInboxes.map((user) => user.email).join(",")
        );
      } else {
        url.searchParams.delete("inboxes");
      }
      
      // Clean up other search parameters
      url.searchParams.delete("messageId");
      url.searchParams.delete("hmid");
      url.searchParams.delete("query");
      
      return url.toString();
    };

    // Combine receivers (to + cc)
    const allReceivers = [
      ...message.toEmails,
      ...message.ccEmails
    ].join("; ");

    return [
      message.headerMessageId || "",
      message.messageId,
      generateLink(),
      message.subject.replace(/"/g, '""'), // Escape quotes in CSV
      formatDate(message.date),
      message.fromEmail,
      allReceivers
    ];
  });

  // Create CSV content
  const csvContent = [
    csvHeaders.join(","),
    ...csvRows.map(row => 
      row.map(field => 
        // Wrap fields containing commas, quotes, or line breaks in quotes
        typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
          ? `"${field}"`
          : field
      ).join(",")
    )
  ].join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const queryStr = searchQuery ? `_${searchQuery.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
    link.setAttribute("download", `email_search_results${queryStr}_${timestamp}.csv`);
    
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};